import React, { useState, useEffect } from 'react'; 
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import logo from "../../images/TestAssessify.png";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { greetTime } from '../account/login';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { api } from '../../api/jointExam';
import Loader from '../../components/loader/Loader';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { clearFlaggedQuestion } from '../../features/candidate-exam/candidate-exam-slice';
import LanguageSwitcher from '../../components/LanguageSwitcher';

type viewType = 'rejoin' | 'join' | null
export const primary_colour = '#88a848'

const JointExam = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState<viewType>('rejoin')
  const [rejoinCode, setRejoinCode] = useState<string>('');
  const [defaultEmail, setDefaultEmail] = useState<string>('')
  const { id } = useParams();

  const { data: userData, isLoading } = useQuery(
    ["User Data"],
    async () => {
      const data = await api.getExam({ id: id as string });
      return data;
    },
    {
      select: (response) => response?.data,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onError: (error: any) => {
        if (
          error?.response?.data?.message === 'Exam does not exist'
          && error?.response?.status === 400
        ) {
          navigate('/page-not-found')
        }
      },
      onSuccess: (data) => {
        if(data?.client){
          sessionStorage.setItem("client", JSON.stringify(data?.client));
        }
      }
    },
  );

  // Allowed domains (lowercase for comparison)
  const allowedDomains: string[] = userData?.exam?.domains_allowed?.length ?
    userData?.exam?.domains_allowed?.map((domain: string) => domain.slice(1))
    : []

  const emailDomainErrorMessage = `Email must be from one of the access bank domains, e.g. your-email@${
    userData?.exam?.domains_allowed?.length ? userData?.exam?.domains_allowed?.join(' ') : null
    }`

  const generateValidationSchema = () => {
  const schema: any = {
    name: Yup.string()
      .trim()
      .matches(/^[a-zA-Z\s]+$/, t('Name can only contain letters and spaces'))
      .required(`${t('Name')} ${t('is-required')}`),
    
    email: Yup.string()
      .email(t('Invalid email'))
      .required(`${t('Email')} ${t('is-required')}`),
  };
  // Add validation for each demographic field
  userData?.exam?.demographicFields?.forEach((field: any) => {
    schema[`demographic_${field.id}`] = Yup.string().required(`${field.label} ${t('is-required')}`);
  });

  return Yup.object().shape(schema);
};


  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const { mutateAsync, isLoading: isSubmitting } = useMutation(api.loginToExamLaunch)
  const { mutateAsync: checkEmail, isLoading: isSubmittingCheckEmail } = useMutation(api.checkExamLaunch)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const successAction = (response: any): void => {
    toast.success(t('candidate-verified'));
    sessionStorage.setItem("userToken", response.data.token);
    sessionStorage.setItem("guestUserExamId", id as string);
    dispatch(clearFlaggedQuestion(null));
    sessionStorage.setItem("user_id", response?.data?.user?.user_id)
    sessionStorage.setItem('attempt_count', response?.data?.exam?.number_of_attempts)
    window.sessionStorage.setItem("active-assessment-id", response?.data?.user?.exam_id);
    window.sessionStorage.setItem("active-assessment-user-id", response?.data?.user?.user_exam_id);
    window.sessionStorage.setItem("active-assessment", JSON.stringify(response?.data?.exam));
    window.sessionStorage.setItem("time_left", response?.data?.exam?.duration);
    window.sessionStorage.setItem("duration", response?.data?.exam?.duration);
    window.sessionStorage.setItem("ql", response?.data?.exam?.number_of_questions);
    window.sessionStorage.setItem("starting-index", response?.data?.exam?.number_of_questions_answered);
    sessionStorage.setItem("user", JSON.stringify(response?.data?.user));
    sessionStorage.setItem("loginType", 'join');
    sessionStorage.setItem("joinType", 'launch');
    if (view === 'rejoin') sessionStorage.setItem("rejoinUser", '0')
    if(!response?.data?.redirect_to_results) {
      return navigate('/candidate/assessments/instructions')
    }
    if (response?.data?.exam?.number_of_attempts < response?.data?.exam?.number_of_attempts_allowed) {
      sessionStorage.setItem('attempt_count', response?.data?.exam?.number_of_attempts)
      navigate(`/candidate/assessments/instructions`)
    } else {
      navigate('/candidate/exam-history')
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      email: '',
      ...(userData?.exam?.demographicFields?.reduce((acc: any, field: any) => {
        acc[`demographic_${field.id}`] = '';
        return acc;
      }, {}) || {})
    },
    validationSchema: generateValidationSchema(),
    onSubmit: async (values): Promise<void> => {
      try {
        // Prepare demographics payload
        const demographics = userData?.exam?.demographicFields?.map((field: any) => ({
          demography_id: field.id,
          value: values[`demographic_${field.id}`]
        }));

        const response = await mutateAsync({
          name: values?.name?.trim(),
          email: values.email,
          exam_id: id,
          returning_user: false,
          existing_user: false,
          demographics
        });
        successAction(response)
      }
      catch (error: any) {
        toast.remove();
        toast.error(error?.response?.data?.message);
      }
    },
  });

  const rejoinExam = async (): Promise<any> => {
     const domain = defaultEmail.split('@')[1]?.toLowerCase();
      if (!defaultEmail) {
        return toast.error(t('email-required'));
      }
      else if (allowedDomains?.length && (!domain || !allowedDomains.includes(domain))) {
        toast.error(emailDomainErrorMessage);
        return;
      }
    try {
      const response = await checkEmail({
        email: defaultEmail,
        client_id: userData?.exam?.client_id,
        exam_id: id
      });
      formik.setFieldValue('email', defaultEmail);
      formik.setFieldValue('name', response?.data?.name);
      if (response?.data?.is_existing) {
        const response = await mutateAsync({
          exam_id: id,
          email: defaultEmail,
          existing_user: true,
        });
        successAction(response)
      }
      else {
        setView('join')
      }
    }
    catch (error: any) {
      toast.remove();
      if (error?.response?.status === 400 || error?.response?.status === 500) {
        return toast.error(error?.response?.data?.message)
      }
      toast.error(t('reload-error'));
    }
  }

  if (isLoading) return <Loader />;

  return (
    <>
      <div className="bg-white p-4 lg:p-6">
        <div className="w-full flex items-center justify-between pb-4 h-26 bg-inherit sticky top-0 cursor-pointer">
          <div className="h-full">
            <Link to={'/'}>
              <img
                src={ userData?.client?.image_url || logo}
                alt="Test Assessify"
                className={ userData?.client?.image_url ? '' : "w-64 h-[60px]"}
              />
            </Link>
          </div>
          <LanguageSwitcher className='border border-slate-300 mr-5 py-3 px-3 rounded-xl focus:border-slate-300 focus:outline-none' containerClassName='flex items-center justify-center' />
        </div>
        <div className="flex justify-center items-center 2xl:mt-0 2xl:h-screen">
          <div className="bg-cover bg-no-repeat bg-hero-img w-full md:w-[600px] lg:w-[900px] 2xl:w-[1440px] 2xl:h-[800px] rounded-xl p-2 lg:p-10 sm:shadow-2xl">
            <div className="my-2 shadow-2xl rounded-[8px] bg-white w-full lg:w-2/3 2xl:h-[700px] 2xl:mt-5 p-4 2xl:p-6">
              <div className="flex flex-col w-full">
                <div className="flex flex-col justify-center lg:px-4 pt-8 my-auto md:justify-start md:pt-0 2xl:px-10">
                  <div className='py-12 pt-3'>
                    <div className="2xl:mb-3">
                      <div className="flex md:items-center justify-start w-full overflow-hidden">
                        <h1 className="text-3xl md:w-3/4 object-contain font-medium text-darkBlue">
                           {t('welcome-to-exam', { title: userData?.exam?.title })}
                        </h1>
                      </div>
                      <p className="my-2 text-md text-slate-500">
                         Please enter your email address to begin.
                      </p>
                    </div>
                    {view === null && (
                      <>
                        <button
                          type="submit"
                          onClick={() => setView('join')}
                          className="w-full mt-8 px-4 2xl:py-4 py-2 rounded-lg text-base font-semibold text-center text-white transition duration-200 ease-in bg-[#88a848] shadow-md focus:outline-none focus:ring-2"
                        >
                          <span className="w-full text-xl 2xl:text-2xl">{t('register-exam')}</span>
                        </button>
                        <p className='text-center my-3'>{t('or')}</p>
                        <button
                          type="button"
                          onClick={() => setView('rejoin')}
                          className="w-full mt-0 px-4 2xl:py-4 py-2 rounded-lg text-base font-semibold text-center text-slate-700 transition duration-200 ease-in bg-slate-300 shadow-md focus:outline-none focus:ring-2"
                        >
                          <span className="w-full text-xl 2xl:text-2xl">{t('rejoin-exam')}</span>
                        </button>
                      </>
                    )}
                  </div>

                  {/* Form Fields */}
                  {view === 'join' && (
                    <form
                      className="flex flex-col md:-mt-10 pt-3 md:pt-0"
                      onSubmit={formik.handleSubmit}
                    >
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-5 pt-5 max-[500px]:-mt-16'>
                        {/* Name Field */}
                        <div>
                          <div className="flex flex-col">
                            <label htmlFor="name" className="text-md font-semibold text-gray-600">
                              { `Full Name`} <span className='text-red-500 text-xl'>*</span>
                            </label>
                            <input
                              type="text"
                              id="name"
                              className="flex-1 appearance-none rounded-lg border border-gray-300 w-full py-4 2xl:py-8 px-4 bg-white text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-[#88a848] focus:border-transparent"
                              placeholder={t('enter-your-name')}
                              {...formik.getFieldProps('name')}
                            />
                            {formik.touched.name && formik.errors.name && (
                              <div className="text-red-500 text-sm mt-2">{formik.errors.name}</div>
                            )}
                          </div>
                        </div>

                        {/* Email Field */}
                        <div>
                          <div className="flex flex-col">
                            <label htmlFor="email" className="text-md font-semibold text-gray-600">
                              {t('Email')} <span className='text-red-500 text-xl'>*</span>
                            </label>
                            <input
                              type="email"
                              id="email"
                              className="flex-1 bg-slate-100 appearance-none rounded-lg border border-gray-300 w-full py-4 2xl:py-8 px-4 text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-[#88a848] focus:border-transparent"
                              placeholder={t('enter-your-email')}
                              disabled
                              {...formik.getFieldProps('email')}
                            />
                            {formik.touched.email && formik.errors.email && (
                              <div className="text-red-500 text-sm mt-2">{formik.errors.email}</div>
                            )}
                          </div>
                        </div>

                        {/* Demographic Fields */}
                        {userData?.exam?.demographicFields?.map((field: any) => (
                          <div key={field.id}>
                            <div className="flex flex-col">
                              <label htmlFor={`demographic_${field.id}`} className="text-md font-semibold text-gray-600">
                                {  field?.label} <span className='text-red-500 text-xl'>*</span>
                              </label>
                              {field.input_type === 'select' ? (
                                <select
                                  id={`demographic_${field.id}`}
                                  className="flex-1 appearance-none rounded-lg border border-gray-300 w-full py-4 2xl:py-8 px-4 bg-white text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-[#88a848] focus:border-transparent"
                                  {...formik.getFieldProps(`demographic_${field.id}`)}
                                >
                                  <option value="">{t('select')} {field.label}</option>
                                  {JSON.parse(field.options).map((option: string, index: number) => (
                                    <option key={index} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type={field.input_type}
                                  id={`demographic_${field.id}`}
                                  className="flex-1 appearance-none rounded-lg border border-gray-300 w-full py-4 2xl:py-8 px-4 bg-white text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-[#88a848] focus:border-transparent"
                                  placeholder={`Enter your ${field.label}`}
                                  {...formik.getFieldProps(`demographic_${field.id}`)}
                                />
                              )}
                              {formik.touched[`demographic_${field.id}`] && formik.errors[`demographic_${field.id}`] && (
                                <div className="text-red-500 text-sm mt-2">{formik.errors[`demographic_${field.id}`]}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        style={{ backgroundColor: userData?.client?.primary_colour || primary_colour }}
                        className="w-full mt-8 px-4 2xl:py-4 py-2 rounded-lg text-base font-semibold text-center text-white transition duration-200 ease-in shadow-md focus:outline-none focus:ring-2"
                      >
                        {isSubmitting ? t('submitting') : <span className="w-full text-xl 2xl:text-2xl">{t('register')}</span>}
                      </button>
                    </form>
                  )}

                  {/* Rejoin Form */}
                  {view === 'rejoin' && (
                    <div className='py-5'>
                      <fieldset className="border border-gray-200 -mt-5 p-4 rounded-xl shadow">
                        <legend className="px-4 text-lg font-semibold text-gray-700 bg-white rounded-md border border-gray-300">
                          {/* {t('work-email')} */} Email Address
                        </legend>
                        <div className="relative flex w-full focus:outline-none focus:ring-2 focus:ring-[#88a848] focus:border-transparent">
                          <input
                            type="email"
                            value={defaultEmail}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              setDefaultEmail(event.target.value);
                            }}
                            className="flex-1 appearance-none border rounded-xl border-gray-300 w-full py-4 px-4 2xl:py-8 bg-white text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none"
                            placeholder='e.g user@gmail.com'
                          />
                        </div>
                        <div className='flex justify-end mt-4'>
                          <button
                            type="button"
                            onClick={rejoinExam}
                            style={{ backgroundColor: userData?.client?.primary_colour || '#88a848' }}
                            className="mt-3 px-12 2xl:py-4 py-2 rounded-lg text-sm text-center text-white transition duration-200 ease-in bg-[#88a848] shadow-md focus:outline-none focus:ring-2"
                          >
                            {isSubmittingCheckEmail ? t('submitting') : <span className="w-full text-lg">{t('start-exam')}</span>}
                          </button>
                        </div>
                      </fieldset>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default JointExam;